import BaseTypeController from "vamos/cockpit/base/controller/BaseTypeController";
import JSONModel from "sap/ui/model/json/JSONModel";
import User from "vamos/cockpit/model/User";
import { Button$PressEvent } from "sap/m/Button";
import { ResponseObject } from "vamos/cockpit/model/api/vamos.private.api.types";
import ApiService from "vamos/cockpit/base/services/type/ApiService";
import Input, { Input$LiveChangeEvent } from "sap/m/Input";
import UserHandler from "vamos/cockpit/data/user/handler/UserHandler";
import Dialog from "sap/m/Dialog";
import ValidationUtil from "vamos/cockpit/base/util/ValidationUtil";
import MessageBox from "sap/m/MessageBox";
import PasswordType from "vamos/cockpit/data/user/settings/model/PasswordType";
import { ValueState } from "sap/ui/core/library";
import { showSuccess } from "vamos/cockpit/base/handler/MessageToastHandler";
import Binding from "sap/ui/model/Binding";
import StringUtil from "vamos/cockpit/base/util/StringUtil";
import ErrorService from "vamos/cockpit/base/services/type/ErrorService";

export default class UserSettingsController extends BaseTypeController {

    private userObject: User;

    public onInit() {
        super.onInit();
        this.viewModel = new JSONModel({
            isEditable: false,
        });
        this.setModel(this.viewModel, "ui");
        this.setModel(new JSONModel(), "password");
    }

    public onAfterRendering() {
        if (!this.userObject) {
            this.userObject = { ...this.getModel("app").getProperty("/User") };
        }
    }

    /* =========================================================== */
    /* event handlers                                              */

    /* =========================================================== */
    public onToggleEdit() {
        this.viewModel.setProperty("/isEditable", !this.viewModel.getProperty("/isEditable"));
    }

    /* =========================================================== */
    /* public methods                                              */
    /* =========================================================== */
    /* =========================================================== */
    /* privat methods                                              */

    /* =========================================================== */
    public async onSave() {
        const oModel = this.getModel("app");
        const oPayload = {
            email: oModel.getProperty("/User/Email"),
            firstName: oModel.getProperty("/User/Firstname"),
            language: oModel.getProperty("/User/Language"),
            lastName: oModel.getProperty("/User/Lastname"),
            userName: oModel.getProperty("/User/Username"),
            isEmailCCEnabled: oModel.getProperty("/User/IsEmailCCEnabled"),
        };

        const fnSuccess = async (oResponse: ResponseObject) => {

        };
        const responseData = await ApiService.getInstance().putRestJsonDataWithoutMessage("/user/updateUserData", oPayload);

        if (responseData.success) {
            await this.successChangePassword();
        } else {
            responseData.data.additionalInfo.forEach((val: { Field: string }) => {
                const oInput = this.byId(val.Field) as Input;
                if (oInput) {
                    oInput.setValueState("Error");
                }

            });
            ErrorService.getInstance().showErrorMessageBox(this.getText("UserSettingSavingError"), this.getText("ErrorTitle"));
        }

        this.userObject = oModel.getProperty("/User");
    }

    private async successChangePassword() {
        this.onToggleEdit();
        const oComponent = this.getComponent();
        await UserHandler.getInstance().loadUser();
        oComponent.refreshUser();
    }

    public async onSavePassword(oEvent: Button$PressEvent) {
        const dialog: Dialog = this.findParentControl("sap.m.Dialog", oEvent.getSource()) as Dialog;
        let passwordModel = this.getModel("password") as JSONModel;

        if (!this.validateFields(dialog)) {
            return;
        }
        const passwordData = passwordModel.getData();

        const responseData = await ApiService.getInstance().postRestJsonDataWithoutMessage("/user/update-password", passwordData);
        if (!responseData.success) {
            this.markErrorField(responseData, dialog);
            await ErrorService.getInstance().showResponseErrors(responseData.data);
            return;
        }

        this.onClosePopup(oEvent, null);
        this.setModel(new JSONModel(), "password");
        showSuccess(this.getText("PasswordChanged"));
    }

    public handlePasswordValidation(event: Input$LiveChangeEvent): void {
        const input: Input = event.getSource();
        this.validatePasswordInput(input);
    }

    public onCancel() {
        this.onToggleEdit();
        this.resetUser();
    }

    private resetUser() {
        UserHandler.getInstance().loadUser().then((oUser: User) => {
            this.getComponent().refreshUser();
        });
    }

    public onCloseChangePasswordPopup(oEvent: Button$PressEvent) {
        this.onClosePopup(oEvent, null);
    }

    private validateFields(dialog: Dialog): boolean {
        if (ValidationUtil.hasEmptyRequiredFields(dialog)) {
            MessageBox.error(this.getText("NoInputs"));
            return false;
        }

        const newPasswordInput = this.getView().byId("NewPasswordInput") as Input;
        const confirmPasswordInput = this.getView().byId("ConfirmNewPasswordInput") as Input;

        const inputs = [newPasswordInput, confirmPasswordInput];
        const hasInputError = inputs.some(input => !this.validatePasswordInput(input));

        if (hasInputError) {
            return false;
        }

        const newPassword = newPasswordInput.getValue();
        const confirmPassword = confirmPasswordInput.getValue();

        if (newPassword !== confirmPassword) {
            inputs.forEach(input => input.setValueState(ValueState.Error));
            MessageBox.error(this.getText("differentPasswords"));
            return false;
        }

        return true;
    }

    private validatePasswordInput(input: Input): boolean {
        if (!input) return false;

        const password: string = input.getValue();
        let errors: string[] = PasswordType.validate(password);

        if (errors.length > 0) {
            errors = errors.map((key: string) => this.getText(key));
            input.setValueStateText(errors.join("\n"));
            input.setValueState(ValueState.Error);
            return false;
        }
        input.setValueState(ValueState.None);
        return true;
    }

    private markErrorField(responseData: any, dialog: Dialog): void {
        const elements: Input[] = dialog.findElements(true).filter(UI5Element => UI5Element instanceof Input);
        responseData.data.additionalInfo.forEach((info: any) => {
            const bindingPath: string = info.Field;

            elements.forEach((element: Input) => {
                const binding: Binding = element.getBinding("value");
                const path = binding.getPath();
                if (StringUtil.normalizeFieldName(bindingPath) === StringUtil.normalizeFieldName(path)) {
                    element.setValueState(ValueState.Error);
                }
            });
        });

    }

}
